"use client";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck } from "@fortawesome/pro-solid-svg-icons/faCheck";
import { faArrowRight } from "@fortawesome/pro-solid-svg-icons/faArrowRight";
import { getUrls } from "@portal/app/_common/lib/urls";
import { useMixpanel } from "@portal/app/_components/hooks/useMixpanel";

export const FormFunnelBanner = ({
  vocationName,
  vocationId,
  industry,
}: {
  vocationName?: string | null;
  vocationId?: string | null;
  industry?: GQL.VocationIndustry;
}) => {
  const analytics = useMixpanel();

  const headline = vocationName
    ? `See if you qualify for ${vocationName} programs`
    : "See if you qualify for training programs";

  return (
    <Container data-testid="blog-page-form-funnel-banner">
      <Heading>
        <IconCircle>
          <FontAwesomeIcon icon={faCheck} aria-hidden="true" />
        </IconCircle>
        <Copy>
          <Headline>{headline}</Headline>
          <Description>Answer a few questions in 2 minutes. Free, no commitment.</Description>
        </Copy>
      </Heading>
      <CTAButton
        variant="contained"
        href={getUrls().FormFunnelHome()}
        endIcon={<FontAwesomeIcon icon={faArrowRight} aria-hidden="true" />}
        data-testid="blog-page-form-funnel-banner-cta"
        onClick={() =>
          analytics.track("Blog - Clicked Form Funnel Banner", {
            vocationId,
            vocationName,
            industry,
          })
        }
      >
        Start
      </CTAButton>
    </Container>
  );
};

const Container = styled("div")`
  width: 100%;
  background: ${({ theme }) => theme.palette.dream.grayLighter};
  border: 2px solid ${({ theme }) => theme.palette.dream.yellow};
  border-radius: 0.75rem;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  box-sizing: border-box;

  ${({ theme }) => theme.breakpoints.down("sm")} {
    padding: 1.25rem;
  }
`;

const Heading = styled("div")`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
`;

const IconCircle = styled("div")`
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: ${({ theme }) => theme.palette.dream.yellow};
  color: ${({ theme }) => theme.palette.dream.grayDark};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
`;

const Copy = styled("div")`
  flex: 1;
  min-width: 0;
`;

const Headline = styled(Typography)`
  font-size: 1.1rem;
  font-weight: 600;
  line-height: 1.3;
  color: ${({ theme }) => theme.palette.dream.grayDark};

  ${({ theme }) => theme.breakpoints.down("sm")} {
    font-size: 1.05rem;
  }
`;

const Description = styled(Typography)`
  margin-top: 0.375rem;
  font-size: 0.95rem;
  line-height: 1.5;
  color: ${({ theme }) => theme.palette.dream.grayMediumDark};

  ${({ theme }) => theme.breakpoints.down("sm")} {
    font-size: 0.9rem;
  }
`;

const CTAButton = styled(Button)`
  width: 100%;
  font-weight: 600;
  font-size: 1rem;
  padding: 0.875rem 1.5rem;
  color: white;
`;
